import { useRef, useState, useEffect } from 'react';
import { useTranslation } from '@klv/hooks/useTranslation';

import Tile from '@klv/molecules/tile';
import { getColorVariantFromBackground, hash, windowIsDefined } from '@klv/shared/utils/functions';
import type { TColorVariant } from '@klv/shared/styles/types';
import Button from '@klv/atoms/button/variants/button';
import useStorage from '@klv/hooks/useStorage';
import * as s from './styles';
import type { ITileList } from './types';

const TileList = ({
  backgroundColor = 'light',
  ctaVariant = 'arrow icon',
  headline = '',
  mainTileHoverColor,
  subhead = '',
  tileVariant,
  tiles,
}: ITileList) => {
  const { t } = useTranslation();

  const tilesId = useRef(`tiles-${hash(JSON.stringify(tiles))}`);
  const [loadedMore, setLoadedMore] = useStorage(tilesId.current, false, 'sessionStorage');
  const [displayAll, setDisplayAll] = useState(false);
  const loadMore = 6;

  const handleResize = () => {
    if (windowIsDefined()) {
      setDisplayAll(window.innerWidth > 1024);
    }
  };

  useEffect(() => {
    if (windowIsDefined()) {
      setDisplayAll(window.innerWidth > 1024);
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <s.StyledTileList $backgroundColor={backgroundColor}>
      <s.StyledGrid>
        {(headline || subhead)
        && (
          <s.HeadlineContainer>
            {headline && <s.StyledHeadline colorVariant={getColorVariantFromBackground(backgroundColor as TColorVariant)} variant="h3">{headline}</s.StyledHeadline>}
            {subhead && <s.StyledSubheadline colorVariant={getColorVariantFromBackground(backgroundColor as TColorVariant)} variant="paragraph1">{subhead}</s.StyledSubheadline>}
          </s.HeadlineContainer>
        )}
      </s.StyledGrid>
      <s.StyledGrid>
        {tiles.map((tile, index) => {
          if (index < loadMore || (loadedMore || displayAll)) {
            return (
              <Tile
                // eslint-disable-next-line react/no-array-index-key
                key={JSON.stringify(tile) + index}
                backgroundColor={backgroundColor}
                headline={tile.headline}
                hoverColor={tile.hoverColor}
                link={tile.link}
                paragraph={tile.paragraph}
                image={tile.image}
                tertiaryButtonText={tile.tertiaryButtonText}
                mainTileHoverColor={mainTileHoverColor}
                openInNewTab={tile.openInNewTab}
                variant={tileVariant}
                ctaVariant={ctaVariant}
              />
            );
          }
          return null;
        })}
      </s.StyledGrid>
      <s.StyledGrid>
        {tiles.length > loadMore
      && !loadedMore && !displayAll
      && (
      <s.ButtonContainer>
        <Button
          action={() => { setLoadedMore(true); }}
          variant="ghost"
          text={t('organisms_tileList_viewAll')}
          buttonColor={backgroundColor === 'light' ? 'dark' : 'light'}
        />
      </s.ButtonContainer>
      )}
      </s.StyledGrid>
    </s.StyledTileList>
  );
};

export default TileList;
